import React from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useToast } from "@/components/ui/use-toast";

const API_BASE =
  import.meta.env.VITE_WEBSITE_ORIGIN || "https://world-class.design";

type DomainStatus =
  | "pending"
  | "verified"
  | "provisioning_ssl"
  | "active"
  | "error";

interface CustomDomain {
  id: string;
  user_id: string;
  apex_domain: string;
  www_domain: string;
  verification_token: string;
  verified_at: string | null;
  status: DomainStatus;
  netlify_domain_id: string | null;
  ssl_status: string | null;
  error_message: string | null;
  created_at: string;
  updated_at: string;
}

interface DomainsResponse {
  success: boolean;
  data: CustomDomain[];
  cap?: number;
  siteDomain?: string | null;
  atCapacity?: boolean;
  error?: string;
}

function statusVariant(status: DomainStatus) {
  switch (status) {
    case "active":
      return "default";
    case "verified":
      return "outline";
    case "provisioning_ssl":
      return "secondary";
    case "error":
      return "destructive";
    default:
      return "secondary";
  }
}

function formatStatus(status: DomainStatus) {
  switch (status) {
    case "provisioning_ssl":
      return "Provisioning SSL";
    default:
      return status.charAt(0).toUpperCase() + status.slice(1);
  }
}

async function fetchDomains(): Promise<DomainsResponse> {
  const response = await fetch(`${API_BASE}/api/domains`, {
    credentials: "include",
  });

  const payload = (await response.json()) as DomainsResponse;

  if (!response.ok || !payload.success) {
    throw new Error(payload.error || "Failed to fetch domains");
  }

  return payload;
}

async function postJson<T>(path: string, body: Record<string, unknown>) {
  const response = await fetch(`${API_BASE}${path}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify(body),
  });

  const payload = (await response.json()) as {
    success?: boolean;
    error?: string;
    [key: string]: unknown;
  };

  if (!response.ok || payload.success === false) {
    throw new Error(payload.error || "Request failed");
  }

  return payload as T;
}

async function deleteJson<T>(path: string, body: Record<string, unknown>) {
  const response = await fetch(`${API_BASE}${path}`, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
    },
    credentials: "include",
    body: JSON.stringify(body),
  });

  const payload = (await response.json()) as {
    success?: boolean;
    error?: string;
    [key: string]: unknown;
  };

  if (!response.ok || payload.success === false) {
    throw new Error(payload.error || "Request failed");
  }

  return payload as T;
}

export default function CustomDomainSettings() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [domainInput, setDomainInput] = React.useState("");
  const [deletingId, setDeletingId] = React.useState<string | null>(null);

  const { data, isLoading, error } = useQuery({
    queryKey: ["custom-domains"],
    queryFn: fetchDomains,
  });

  const domains = data?.data || [];
  const siteDomain = data?.siteDomain || "<your-site>.netlify.app";
  const atCapacity = data?.atCapacity === true;

  const addDomainMutation = useMutation({
    mutationFn: async () => {
      if (!domainInput.trim()) {
        throw new Error("Enter a domain to connect");
      }

      return postJson("/api/domains", { domain: domainInput.trim() });
    },
    onSuccess: () => {
      setDomainInput("");
      queryClient.invalidateQueries({ queryKey: ["custom-domains"] });
      toast({
        title: "Domain added",
        description: "Add the DNS records to continue verification.",
      });
    },
    onError: (err: Error) => {
      toast({
        title: "Unable to add domain",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const verifyMutation = useMutation({
    mutationFn: async (domainId: string) =>
      postJson("/api/domains/verify", { domainId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["custom-domains"] });
      toast({
        title: "Domain verified",
        description: "Next, provision SSL to activate your domain.",
      });
    },
    onError: (err: Error) => {
      toast({
        title: "Verification failed",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const provisionMutation = useMutation({
    mutationFn: async (domainId: string) =>
      postJson("/api/domains/provision", { domainId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["custom-domains"] });
      toast({
        title: "Provisioning started",
        description: "We are waiting for SSL to be issued.",
      });
    },
    onError: (err: Error) => {
      toast({
        title: "Provisioning failed",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const checkSslMutation = useMutation({
    mutationFn: async (domainId: string) =>
      postJson("/api/domains/check-ssl", { domainId }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["custom-domains"] });
      toast({
        title: "SSL status updated",
        description: "We refreshed the SSL status from Netlify.",
      });
    },
    onError: (err: Error) => {
      toast({
        title: "SSL check failed",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async (domainId: string) =>
      deleteJson("/api/domains", { domainId }),
    onMutate: (domainId) => {
      setDeletingId(domainId);
      return { domainId };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["custom-domains"] });
      toast({
        title: "Domain removed",
        description: "The custom domain has been deleted.",
      });
    },
    onError: (err: Error) => {
      toast({
        title: "Unable to delete domain",
        description: err.message,
        variant: "destructive",
      });
    },
    onSettled: () => {
      setDeletingId(null);
    },
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle>Custom Domains</CardTitle>
        <p className="text-sm text-muted-foreground">
          Connect a custom domain for your public profile. DNS propagation time
          varies by provider.
        </p>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="space-y-2">
          <label className="text-sm font-medium">Add a domain</label>
          <div className="flex flex-col gap-2 sm:flex-row">
            <Input
              placeholder="yourdomain.com"
              value={domainInput}
              onChange={(event) => setDomainInput(event.target.value)}
              disabled={addDomainMutation.isPending || atCapacity}
            />
            <Button
              onClick={() => addDomainMutation.mutate()}
              disabled={addDomainMutation.isPending || atCapacity}
            >
              {addDomainMutation.isPending ? "Adding..." : "Add domain"}
            </Button>
          </div>
          {atCapacity && (
            <Alert variant="destructive">
              <AlertDescription>
                Custom domains are currently at capacity. We&apos;ll notify you
                when more slots become available.
              </AlertDescription>
            </Alert>
          )}
        </div>

        {isLoading && (
          <p className="text-sm text-muted-foreground">Loading domains...</p>
        )}

        {error && (
          <Alert variant="destructive">
            <AlertDescription>{(error as Error).message}</AlertDescription>
          </Alert>
        )}

        {domains.length > 0 && (
          <div className="space-y-4">
            {domains.map((domain) => (
              <div
                key={domain.id}
                className="rounded-lg border border-gray-200 p-4 space-y-3"
              >
                <div className="flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold">{domain.www_domain}</p>
                    <p className="text-xs text-muted-foreground">
                      Apex: {domain.apex_domain}
                    </p>
                  </div>
                  <Badge variant={statusVariant(domain.status)}>
                    {formatStatus(domain.status)}
                  </Badge>
                </div>

                {domain.status === "pending" && (
                  <div className="space-y-2 text-sm">
                    <p className="font-medium">DNS records to add</p>
                    <div className="rounded-md bg-muted p-3 text-xs font-mono space-y-2">
                      <div>
                        TXT _wcverify.{domain.apex_domain} = {" "}
                        {domain.verification_token}
                      </div>
                      <div>
                        CNAME www → {siteDomain}
                      </div>
                      <div>
                        Apex: ALIAS/ANAME/flattened CNAME → {siteDomain} or URL
                        redirect → https://www.{domain.apex_domain}
                      </div>
                    </div>
                  </div>
                )}

                {domain.error_message && (
                  <Alert variant="destructive">
                    <AlertDescription>{domain.error_message}</AlertDescription>
                  </Alert>
                )}

                <div className="flex flex-wrap gap-2">
                  {domain.status === "pending" && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => verifyMutation.mutate(domain.id)}
                      disabled={verifyMutation.isPending}
                    >
                      Verify TXT
                    </Button>
                  )}

                  {domain.status === "verified" && (
                    <Button
                      size="sm"
                      onClick={() => provisionMutation.mutate(domain.id)}
                      disabled={provisionMutation.isPending}
                    >
                      Provision SSL
                    </Button>
                  )}

                  {domain.status === "error" && domain.verified_at && (
                    <Button
                      size="sm"
                      onClick={() => provisionMutation.mutate(domain.id)}
                      disabled={provisionMutation.isPending}
                    >
                      Retry Provision
                    </Button>
                  )}

                  {domain.status === "provisioning_ssl" && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => checkSslMutation.mutate(domain.id)}
                      disabled={checkSslMutation.isPending}
                    >
                      Check SSL status
                    </Button>
                  )}

                  {domain.status === "active" && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => checkSslMutation.mutate(domain.id)}
                      disabled={checkSslMutation.isPending}
                    >
                      Refresh SSL
                    </Button>
                  )}

                  <Button
                    variant="destructive"
                    size="sm"
                    onClick={() => {
                      const confirmed = window.confirm(
                        `Delete ${domain.www_domain}? This cannot be undone.`
                      );
                      if (confirmed) {
                        deleteMutation.mutate(domain.id);
                      }
                    }}
                    disabled={
                      deleteMutation.isPending && deletingId === domain.id
                    }
                  >
                    {deleteMutation.isPending && deletingId === domain.id
                      ? "Deleting..."
                      : "Delete"}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
